import { StandardEvents } from '@shopify/events';

class UMProductDock extends HTMLElement {
  connectedCallback() {
    this._abort = new AbortController();
    const signal = this._abort.signal;
    this.root = document.getElementById(this.dataset.rootId);
    this.select = this.querySelector('[data-um-dock-select]');
    this.button = this.querySelector('[data-um-dock-add]');
    if (!this.root || !this.select || !this.button) return;
    this.pending = false;
    this.busy = false;
    this.invalid = false;
    this.generation = 0;
    this.root.umAddToCart = () => this.addProduct();
    this.root.umPurchaseCoordinator = this;
    this.root.addEventListener('submit', event => {
      if (this.busy || this.root.dataset.umPurchasePending === 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { signal, capture: true });
    window.addEventListener('pageshow', event => {
      if (!event.persisted) return;
      delete this.root.dataset.umPurchasePending;
      this.busy = false;
      this.addRequestActive = false;
      this.root.querySelectorAll('[data-um-buy-now]').forEach(button => {
        button.textContent = 'Acquista ora';
        button.removeAttribute('aria-busy');
      });
      this.refreshControls();
    }, { signal });
    this.select.addEventListener('change', () => {
      const chosen = this.select.value;
      if (typeof this.root.umSelectVariant === 'function' && this.root.umSelectVariant(chosen)) this.sync(chosen);
    }, { signal });
    this.root.addEventListener('um:variant-changed', event => this.sync(event.detail.id), { signal });
    this.button.addEventListener('click', () => this.addProduct(), { signal });
    this.root.querySelector('[data-add]')?.addEventListener('click', () => this.addProduct(), { signal });
    const target = () => window.matchMedia('(max-width:749px)').matches
      ? this.root.querySelector('[data-add]')
      : this.root.querySelector('.buy-buttons-block');
    const watch = () => {
      this.observer?.disconnect();
      const node = target();
      if (!node || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(entries => {
        const entry = entries[0];
        this.passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.visibility();
      });
      this.observer.observe(node);
    };
    watch();
    window.addEventListener('resize', watch, { signal, passive: true });
    document.addEventListener('theme-drawer:open', () => this.visibility(), { signal });
    document.addEventListener('theme-drawer:close', () => this.visibility(), { signal });
    document.addEventListener('um:menu-state', () => this.visibility(), { signal });
    this.attributeObserver = new MutationObserver(() => this.visibility());
    this.attributeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    const drawer = document.getElementById('cart-drawer');
    if (drawer) this.attributeObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    const sizeModal = this.root.querySelector('[data-size-modal]');
    if (sizeModal) this.attributeObserver.observe(sizeModal, { attributes: true, attributeFilter: ['hidden'] });
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, event => {
      if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
      const generation = ++this.generation;
      this.pending = true;
      this.variantRequest = event.promise;
      this.refreshControls();
      Promise.resolve(event.promise).then(({ detail }) => {
        if (generation !== this.generation) return;
        if (detail?.productId && String(detail.productId) !== this.dataset.productId) {
          this.invalid = true;
          return;
        }
        const id = detail?.resource?.id;
        this.invalid = !id || detail?.resource?.available === false;
        if (id) {
          this.root.dispatchEvent(new CustomEvent('um:sync-variant', { detail: { id: String(id) } }));
          this.sync(id);
        }
      }).catch(error => {
        if (generation !== this.generation) return;
        this.invalid = true;
        if (error?.name !== 'AbortError') this.showError('Non è stato possibile aggiornare la taglia. Selezionala di nuovo.');
      }).finally(() => {
        if (generation !== this.generation) return;
        this.pending = false;
        this.refreshControls();
      });
    }, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, event => {
      if (!this.isOwnEvent(event) || event.action !== 'add') return;
      this.addRequestActive = true;
      this.busy = true;
      this.refreshControls();
      const request = event.promise;
      this.cartRequest = request;
      Promise.resolve(request).then(({ detail }) => {
        if (this.cartRequest !== request) return;
        if (detail?.didError) this.showError('Articolo non aggiunto. Verifica la disponibilità.');
      }).catch(() => {
        if (this.cartRequest === request) this.showError('Non è stato possibile aggiungere l’articolo. Riprova.');
      }).finally(() => {
        if (this.cartRequest !== request) return;
        this.busy = false;
        this.addRequestActive = false;
        this.refreshControls();
      });
    }, { signal });
    document.addEventListener(StandardEvents.cartError, event => {
      if (!this.isOwnEvent(event)) return;
      this.showError(event.detail?.description || event.detail?.error || event.error || 'Non è stato possibile aggiungere l’articolo. Riprova.');
    }, { signal });
    this.sync(this.dataset.currentVariant);
  }
  isOwnEvent(event) {
    return event.target instanceof Element && this.root.contains(event.target) && !event.target.closest('product-card');
  }
  addProduct() {
    if (this.pending || this.busy || this.invalid || this.button.disabled) return;
    const form = this.root.querySelector('.rp-fast__native form[action*="/cart/add"]');
    const native = form?.querySelector('button[name="add"],button[type="submit"]');
    if (!form || !native || native.disabled) {
      this.showError('Verifica la taglia e la disponibilità prima di aggiungere l’articolo.');
      return;
    }
    if (!form.reportValidity()) return;
    this.clearErrors();
    this.addRequestActive = false;
    try {
      form.requestSubmit(native);
      queueMicrotask(() => {
        if (!this.addRequestActive && !this.busy) {
          const nativeError = this.root.querySelector('[ref="addToCartTextError"]:not(.hidden)');
          if (nativeError?.textContent.trim()) this.showError(nativeError.textContent.trim());
        }
      });
    } catch (_) {
      this.showError('Non è stato possibile aggiungere l’articolo. Riprova.');
    }
  }
  sync(id) {
    const option = Array.from(this.select.options).find(item => String(item.value) === String(id));
    if (!option) return;
    this.select.value = option.value;
    this.dataset.currentVariant = option.value;
    this.querySelector('[data-um-dock-price]').textContent = option.dataset.price;
    const image = this.querySelector('.um-product-dock__image');
    if (image && option.dataset.image) {
      image.src = option.dataset.image;
      image.removeAttribute('srcset');
    }
    this.refreshControls();
  }
  refreshControls() {
    const available = this.select.selectedOptions[0]?.dataset.available === 'true' && !this.invalid;
    const purchasePending = this.root.dataset.umPurchasePending === 'true';
    const disabled = !available || this.pending || this.busy || purchasePending;
    const label = !available ? 'Non disponibile' : this.pending ? 'Aggiornamento…' : this.busy ? 'Aggiunta…' : 'Aggiungi al carrello';
    this.button.disabled = disabled;
    this.button.textContent = label;
    if (this.busy) this.button.setAttribute('aria-busy', 'true');
    else this.button.removeAttribute('aria-busy');
    this.select.disabled = this.busy || purchasePending;
    const nativeAdd = this.root.querySelector('.rp-fast__native button[name="add"]');
    const nativeId = this.root.querySelector('.rp-fast__native [name="id"]')?.value;
    if (nativeAdd && (this.busy || purchasePending)) {
      if (!this.nativeLock) this.nativeLock = { node: nativeAdd, disabled: nativeAdd.disabled, id: nativeId };
      nativeAdd.disabled = true;
    } else if (this.nativeLock) {
      if (nativeAdd === this.nativeLock.node) {
        nativeAdd.disabled = !available || this.pending || (nativeId === this.nativeLock.id && this.nativeLock.disabled);
      }
      this.nativeLock = null;
    }
    if (purchasePending) {
      if (!this.optionLock) {
        this.optionLock = Array.from(this.root.querySelectorAll('variant-picker input,variant-picker select,[data-opt-index]')).map(node => ({ node, disabled: node.disabled }));
      }
      this.optionLock.forEach(({ node }) => { node.disabled = true; });
    } else if (this.optionLock) {
      this.optionLock.forEach(({ node, disabled }) => { if (node.isConnected) node.disabled = disabled; });
      this.optionLock = null;
    }
    this.root.querySelectorAll('[data-add],[data-um-buy-now]').forEach(button => {
      button.disabled = disabled;
      if (button.matches('[data-add]')) {
        button.textContent = label;
        if (this.busy) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
      }
    });
  }
  showError(message) {
    [this.querySelector('[data-um-dock-error]'), this.root.querySelector('[data-rp-mobile] [data-um-purchase-error]')].forEach(node => {
      if (node) { node.textContent = message; node.hidden = false; }
    });
  }
  clearErrors() {
    this.querySelector('[data-um-dock-error]')?.setAttribute('hidden', '');
    this.root.querySelectorAll('[data-um-purchase-error]').forEach(node => { node.hidden = true; });
  }
  visibility() {
    const menuOpen = document.body.classList.contains('np-hh-lock');
    const drawerOpen = document.getElementById('cart-drawer')?.hasAttribute('open');
    const modalOpen = Boolean(document.querySelector('dialog[open], [data-size-modal]:not([hidden])'));
    this.hidden = !this.passed || menuOpen || drawerOpen || modalOpen;
    document.body.classList.toggle('um-dock-visible', !this.hidden);
  }
  disconnectedCallback() {
    this._abort?.abort();
    this.observer?.disconnect();
    this.attributeObserver?.disconnect();
    if (this.root?.umPurchaseCoordinator === this) {
      delete this.root.umAddToCart;
      delete this.root.umPurchaseCoordinator;
    }
    document.body.classList.remove('um-dock-visible');
  }
}
if (!customElements.get('um-product-dock')) customElements.define('um-product-dock', UMProductDock);

const purchasePending = new WeakSet();
document.addEventListener('click', async event => {
  const button = event.target.closest?.('[data-um-buy-now]');
  if (!button || button.disabled) return;
  event.preventDefault();
  const root = button.closest('.rp-fast');
  let form = button.closest('form') || root?.querySelector('.rp-fast__native form[action*="/cart/add"]');
  const coordinator = root?.umPurchaseCoordinator;
  if (!form || !form.reportValidity() || purchasePending.has(form) || coordinator?.pending || coordinator?.invalid || coordinator?.busy) return;
  const operationForm = form;
  purchasePending.add(operationForm);
  const error = button.parentElement.querySelector('[data-um-purchase-error]') || root?.querySelector('[data-um-purchase-error]');
  if (error) error.hidden = true;
  if (root) root.dataset.umPurchasePending = 'true';
  coordinator?.refreshControls();
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  const original = button.textContent;
  button.textContent = 'Preparazione…';
  try {
    const data = new FormData(form);
    if (!data.get('id')) throw new Error('Seleziona una variante disponibile.');
    data.delete('sections');
    data.delete('sections_url');
    const localeRoot = window.Shopify?.routes?.root || '/';
    const response = await fetch(localeRoot + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: data });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || result.message || 'Articolo non disponibile.');
    window.location.assign(localeRoot + 'checkout');
  } catch (err) {
    if (error) { error.textContent = err.message || 'Non è stato possibile proseguire. Riprova.'; error.hidden = false; }
    if (root) delete root.dataset.umPurchasePending;
    button.removeAttribute('aria-busy');
    button.textContent = original;
    if (coordinator) coordinator.refreshControls();
    else button.disabled = !form.querySelector('[name="id"]')?.value;
  } finally {
    purchasePending.delete(operationForm);
  }
});